import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Plus, Edit2, Trash2, X, Code, Eye, EyeOff, Copy, Check, BarChart3, RotateCcw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_OPTIONS = [
  { value: 'google', label: 'Google', color: '#4285F4' },
  { value: 'meta', label: 'Meta (Facebook/Instagram)', color: '#0668E1' },
  { value: 'tiktok', label: 'TikTok', color: '#000000' },
  { value: 'linkedin', label: 'LinkedIn', color: '#0A66C2' },
  { value: 'custom', label: 'Custom', color: '#6B7280' },
];

const PLACEMENT_OPTIONS = [
  { value: 'head', label: '<head>' },
  { value: 'body-top', label: '<body> (top)' },
  { value: 'body-bottom', label: '<body> (bottom)' },
];

const ENVIRONMENT_OPTIONS = [
  { value: 'both', label: 'Both (Production & Staging)' },
  { value: 'production', label: 'Production Only' },
  { value: 'staging', label: 'Staging Only' },
];

export default function TrackingScriptsManager() {
  const { getAuthHeader } = useAuth();
  const [scripts, setScripts] = useState([]);
  const [availablePages, setAvailablePages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingScript, setEditingScript] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    platform: 'google',
    script_code: '',
    dedup_key: '',
    placement: 'head',
    is_enabled: true,
    environment: 'both',
    selected_pages: [],
  });

  const fetchScripts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/tracking-scripts`,
        { headers: getAuthHeader() }
      );
      setScripts(response.data);
    } catch (error) {
      console.error('Error fetching tracking scripts:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  const fetchAvailablePages = useCallback(async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/tracking-scripts/pages`,
        { headers: getAuthHeader() }
      );
      setAvailablePages(response.data);
    } catch (error) {
      console.error('Error fetching available pages:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchScripts();
    fetchAvailablePages();
  }, [fetchScripts, fetchAvailablePages]);

  const resetForm = () => {
    setFormData({
      name: '',
      platform: 'google',
      script_code: '',
      dedup_key: '',
      placement: 'head',
      is_enabled: true,
      environment: 'both',
      selected_pages: [],
    });
    setEditingScript(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingScript) {
        await axios.put(
          `${BACKEND_URL}/api/admin/tracking-scripts/${editingScript.id}`,
          formData,
          { headers: getAuthHeader() }
        );
      } else {
        await axios.post(
          `${BACKEND_URL}/api/admin/tracking-scripts`,
          formData,
          { headers: getAuthHeader() }
        );
      }
      
      setShowModal(false);
      resetForm();
      fetchScripts();
    } catch (error) {
      console.error('Error saving tracking script:', error);
      alert('Failed to save tracking script: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (script) => {
    setEditingScript(script);
    setFormData({
      name: script.name,
      platform: script.platform,
      script_code: script.script_code,
      dedup_key: script.dedup_key,
      placement: script.placement,
      is_enabled: script.is_enabled,
      environment: script.environment || 'both',
      selected_pages: script.selected_pages || [],
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this tracking script?')) return;
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/tracking-scripts/${id}`,
        { headers: getAuthHeader() }
      );
      fetchScripts();
    } catch (error) {
      console.error('Error deleting tracking script:', error);
      alert('Failed to delete tracking script');
    }
  };

  const handleToggle = async (script) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/tracking-scripts/${script.id}/toggle`,
        {},
        { headers: getAuthHeader() }
      );
      fetchScripts();
    } catch (error) {
      console.error('Error toggling tracking script:', error);
    }
  };

  const handleResetCount = async (script) => {
    if (!window.confirm(`Are you sure you want to reset the load count for "${script.name}"?`)) return;
    
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/tracking-scripts/${script.id}/reset-count`,
        {},
        { headers: getAuthHeader() }
      );
      fetchScripts();
    } catch (error) {
      console.error('Error resetting count:', error);
      alert('Failed to reset count');
    }
  };

  const handlePageSelection = (pageId) => {
    setFormData(prev => ({
      ...prev,
      selected_pages: prev.selected_pages.includes(pageId)
        ? prev.selected_pages.filter(id => id !== pageId)
        : [...prev.selected_pages, pageId]
    }));
  };

  const selectAllPages = () => {
    setFormData(prev => ({
      ...prev,
      selected_pages: availablePages.map(p => p.id)
    }));
  };

  const clearAllPages = () => {
    setFormData(prev => ({
      ...prev,
      selected_pages: []
    }));
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getPlatformInfo = (platform) => {
    return PLATFORM_OPTIONS.find(p => p.value === platform) || PLATFORM_OPTIONS[4];
  };

  const getPageNames = (pageIds) => {
    if (!pageIds || pageIds.length === 0) return 'All Pages';
    return pageIds.map(id => {
      const page = availablePages.find(p => p.id === id);
      return page ? page.name : id;
    }).join(', ');
  };

  return (
    <div>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
            Manage tracking pixels and scripts for Google Analytics, Meta Pixel, TikTok, LinkedIn, and custom tracking codes.
          </p>
        </div>
        <button
          className="btn-primary"
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <Plus size={20} />
          Add Tracking Script
        </button>
      </div>

      {/* Info Banner */}
      <div style={{ 
        background: '#f0f9ff', 
        border: '1px solid #bae6fd', 
        borderRadius: '8px', 
        padding: '16px', 
        marginBottom: '24px' 
      }}>
        <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#0369a1' }}>
          <Code size={16} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
          How Tracking Scripts Work
        </h4>
        <ul style={{ margin: '0', paddingLeft: '24px', fontSize: '13px', color: '#0369a1', lineHeight: '1.6' }}>
          <li>Scripts are automatically injected on public pages only (excluding admin, login, and CRM pages)</li>
          <li>Use the <strong>deduplication key</strong> to prevent duplicate loading on SPA navigation</li>
          <li>Scripts load asynchronously and fail silently - they will not break your website</li>
          <li>Select specific pages or leave empty to load on all public pages</li>
        </ul>
      </div>

      {/* Scripts List */}
      {loading ? (
        <div className="loading">Loading tracking scripts...</div>
      ) : scripts.length === 0 ? (
        <div className="empty-state" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <Code size={48} style={{ margin: '0 auto 16px', display: 'block', opacity: 0.5 }} />
          <p style={{ margin: '0 0 8px 0', fontWeight: '500' }}>No tracking scripts configured</p>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
            Add your first tracking pixel to start collecting analytics data.
          </p>
        </div>
      ) : (
        <div className="integrations-grid">
          {scripts.map(script => {
            const platformInfo = getPlatformInfo(script.platform);
            return (
              <div key={script.id} className="integration-card" style={{ position: 'relative' }}>
                {/* Platform Badge */}
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  background: platformInfo.color,
                  color: 'white',
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '600',
                  textTransform: 'uppercase'
                }}>
                  {platformInfo.label}
                </div>

                <div className="integration-header" style={{ paddingRight: '100px' }}>
                  <div>
                    <h3 className="heading-4" style={{ marginBottom: '4px' }}>{script.name}</h3>
                    <p className="caption" style={{ fontSize: '12px' }}>
                      Placement: <code style={{ background: '#f3f4f6', padding: '2px 6px', borderRadius: '3px' }}>{script.placement}</code>
                    </p>
                  </div>
                </div>
                
                <div className="integration-details" style={{ marginTop: '12px' }}>
                  {/* Dedup Key */}
                  <div style={{ marginBottom: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <strong style={{ fontSize: '12px', color: '#6b7280' }}>Dedup Key:</strong>
                      <button
                        onClick={() => copyToClipboard(script.dedup_key, `dedup-${script.id}`)}
                        style={{ 
                          padding: '2px 6px', 
                          fontSize: '10px', 
                          background: copiedId === `dedup-${script.id}` ? '#10b981' : '#f3f4f6',
                          color: copiedId === `dedup-${script.id}` ? 'white' : '#374151',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        {copiedId === `dedup-${script.id}` ? <Check size={10} /> : <Copy size={10} />}
                        {copiedId === `dedup-${script.id}` ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <code style={{ 
                      fontSize: '11px', 
                      background: '#f9fafb', 
                      padding: '4px 8px', 
                      borderRadius: '4px',
                      display: 'block',
                      marginTop: '4px',
                      wordBreak: 'break-all'
                    }}>
                      {script.dedup_key}
                    </code>
                  </div>

                  {/* Environment */}
                  <p className="body-small" style={{ marginBottom: '4px' }}>
                    <strong>Environment:</strong> {script.environment === 'both' ? 'Production & Staging' : script.environment}
                  </p>

                  {/* Pages */}
                  <p className="body-small" style={{ marginBottom: '4px' }}>
                    <strong>Pages:</strong> {getPageNames(script.selected_pages)}
                  </p>

                  {/* Load Count */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    marginTop: '8px',
                    padding: '8px 12px',
                    background: '#f0fdf4',
                    borderRadius: '6px',
                    border: '1px solid #bbf7d0'
                  }}>
                    <BarChart3 size={16} style={{ color: '#16a34a' }} />
                    <div>
                      <span style={{ fontSize: '18px', fontWeight: '700', color: '#16a34a' }}>
                        {(script.load_count || 0).toLocaleString()}
                      </span>
                      <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '6px' }}>
                        loads
                      </span>
                    </div>
                    {script.last_loaded_at && (
                      <span style={{ fontSize: '10px', color: '#9ca3af', marginLeft: 'auto' }}>
                        Last: {new Date(script.last_loaded_at).toLocaleDateString()}
                      </span>
                    )}
                    {(script.load_count || 0) > 0 && (
                      <button
                        onClick={() => handleResetCount(script)}
                        style={{
                          marginLeft: script.last_loaded_at ? '8px' : 'auto',
                          padding: '4px 8px',
                          fontSize: '10px',
                          background: '#fee2e2',
                          color: '#dc2626',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                        title="Reset count"
                      >
                        <RotateCcw size={10} />
                        Reset
                      </button>
                    )}
                  </div>

                  {/* Status */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    marginTop: '8px'
                  }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 10px',
                      background: script.is_enabled ? '#d1fae5' : '#fee2e2',
                      color: script.is_enabled ? '#065f46' : '#991b1b',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '600'
                    }}>
                      {script.is_enabled ? <Eye size={12} /> : <EyeOff size={12} />}
                      {script.is_enabled ? 'Active' : 'Disabled'}
                    </span>
                  </div>
                </div>

                <div className="integration-actions" style={{ marginTop: '16px' }}>
                  <button
                    className="btn-secondary"
                    onClick={() => handleToggle(script)}
                    style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    {script.is_enabled ? <EyeOff size={16} /> : <Eye size={16} />}
                    {script.is_enabled ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={() => handleEdit(script)}
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    className="btn-danger"
                    onClick={() => handleDelete(script.id)}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div 
            className="modal-content" 
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '600px', maxHeight: '90vh', overflow: 'auto' }}
          >
            <div className="modal-header">
              <h2>{editingScript ? 'Edit Tracking Script' : 'Add Tracking Script'}</h2>
              <button onClick={() => setShowModal(false)} className="modal-close">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Name */}
              <div className="form-group">
                <label>Script Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Google Analytics 4, Meta Pixel"
                  required
                />
              </div>

              {/* Platform */}
              <div className="form-group">
                <label>Platform *</label>
                <select
                  value={formData.platform}
                  onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                  required
                >
                  {PLATFORM_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Dedup Key */}
              <div className="form-group">
                <label>Deduplication Key *</label>
                <input
                  type="text"
                  value={formData.dedup_key}
                  onChange={(e) => setFormData({ ...formData, dedup_key: e.target.value })}
                  placeholder="e.g., ga4-main, meta-pixel-123, tiktok-conversion"
                  required
                />
                <small style={{ color: '#6b7280', fontSize: '12px' }}>
                  Unique identifier to prevent duplicate script loading. Use lowercase with hyphens.
                </small>
              </div>

              {/* Script Code */}
              <div className="form-group">
                <label>Script Code *</label>
                <textarea
                  value={formData.script_code}
                  onChange={(e) => setFormData({ ...formData, script_code: e.target.value })}
                  placeholder="Paste your tracking script code here (including <script> tags if needed)"
                  rows={8}
                  required
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />
                <small style={{ color: '#6b7280', fontSize: '12px' }}>
                  Paste the entire tracking code provided by the platform (including &lt;script&gt; tags).
                </small>
              </div>

              {/* Placement */}
              <div className="form-group">
                <label>Placement *</label>
                <select
                  value={formData.placement}
                  onChange={(e) => setFormData({ ...formData, placement: e.target.value })}
                  required
                >
                  {PLACEMENT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <small style={{ color: '#6b7280', fontSize: '12px' }}>
                  Most tracking pixels should be placed in &lt;head&gt;. Check platform documentation if unsure.
                </small>
              </div>

              {/* Environment */}
              <div className="form-group">
                <label>Environment</label>
                <select
                  value={formData.environment}
                  onChange={(e) => setFormData({ ...formData, environment: e.target.value })}
                >
                  {ENVIRONMENT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Page Selection */}
              <div className="form-group">
                <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Select Pages</span>
                  <span style={{ fontSize: '12px', fontWeight: 'normal' }}>
                    <button 
                      type="button" 
                      onClick={selectAllPages}
                      style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '12px', marginRight: '12px' }}
                    >
                      Select All
                    </button>
                    <button 
                      type="button" 
                      onClick={clearAllPages}
                      style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '12px' }}
                    >
                      Clear All
                    </button>
                  </span>
                </label>
                <div style={{ 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '8px', 
                  padding: '12px',
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {availablePages.map(page => (
                    <label 
                      key={page.id} 
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px',
                        padding: '8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        background: formData.selected_pages.includes(page.id) ? '#eff6ff' : 'transparent'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.selected_pages.includes(page.id)}
                        onChange={() => handlePageSelection(page.id)}
                      />
                      <span style={{ flex: 1 }}>
                        <strong style={{ fontSize: '13px' }}>{page.name}</strong>
                        <span style={{ 
                          display: 'block', 
                          fontSize: '11px', 
                          color: '#6b7280',
                          fontFamily: 'monospace'
                        }}>
                          {page.path}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
                <small style={{ color: '#6b7280', fontSize: '12px' }}>
                  Leave empty to load script on all public pages. Scripts never load on admin/login pages.
                </small>
              </div>

              {/* Enable/Disable */}
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.is_enabled}
                    onChange={(e) => setFormData({ ...formData, is_enabled: e.target.checked })}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <span>Enable this tracking script</span>
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingScript ? 'Update Script' : 'Add Script'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
